/**
 * Resource library API service
 */

import { get, post, put, del } from '../utils/request';
import {
  ResourceFolder,
  ResourceFolderCreate,
  ResourceFolderUpdate,
  Copywriting,
  CopywritingCreate,
  CopywritingUpdate,
  CopywritingUse,
  CopywritingUseResponse,
  CopywritingListResponse,
  Material,
  MaterialSimple,
  MaterialCreate,
  MaterialUpdate,
  MaterialListResponse,
  QuickReplyItem,
  ResourceQueryParams,
  QuickReplyQueryParams,
  TrendingQueryParams,
  FolderType,
} from '../types/resource';

const API_BASE = '/resources';

// Resource Folder APIs
export const folderApi = {
  // Get folder list
  getList: async (params: { folder_type?: string; scope?: string; owner_id?: number }): Promise<{ items: ResourceFolder[]; total: number }> => {
    const result = await get<ResourceFolder[]>(`${API_BASE}/folders`, { params });
    return {
      items: Array.isArray(result) ? result : [],
      total: Array.isArray(result) ? result.length : 0,
    };
  },

  // Create folder
  create: async (data: ResourceFolderCreate): Promise<ResourceFolder> => {
    return post<ResourceFolder>(`${API_BASE}/folders`, data);
  },

  // Get folder tree
  getTree: async (folderType: FolderType, scope: string = 'all'): Promise<ResourceFolder[]> => {
    return get<ResourceFolder[]>(`${API_BASE}/folders`, {
      params: { folder_type: folderType, scope },
    });
  },

  // Get folder by ID
  getById: async (id: number): Promise<ResourceFolder> => {
    return get<ResourceFolder>(`${API_BASE}/folders/${id}`);
  },

  // Update folder
  update: async (id: number, data: ResourceFolderUpdate): Promise<ResourceFolder> => {
    return put<ResourceFolder>(`${API_BASE}/folders/${id}`, data);
  },

  // Delete folder
  delete: async (id: number): Promise<void> => {
    return del<void>(`${API_BASE}/folders/${id}`);
  },

  // Move folder
  move: async (id: number, targetParentId: number | null): Promise<ResourceFolder> => {
    return post<ResourceFolder>(`${API_BASE}/folders/${id}/move`, {
      target_parent_id: targetParentId,
    });
  },
};

// Alias for backward compatibility
export const resourceFolderApi = folderApi;

// Copywriting APIs
export const copywritingApi = {
  // Create copywriting
  create: async (data: CopywritingCreate): Promise<Copywriting> => {
    return post<Copywriting>(`${API_BASE}/copywriting`, data);
  },

  // Get copywriting list
  getList: async (params: ResourceQueryParams): Promise<CopywritingListResponse> => {
    return get<CopywritingListResponse>(`${API_BASE}/copywriting`, { params });
  },

  // Get copywriting by ID
  getById: async (id: number): Promise<Copywriting> => {
    return get<Copywriting>(`${API_BASE}/copywriting/${id}`);
  },

  // Update copywriting
  update: async (id: number, data: CopywritingUpdate): Promise<Copywriting> => {
    return put<Copywriting>(`${API_BASE}/copywriting/${id}`, data);
  },

  // Delete copywriting
  delete: async (id: number): Promise<void> => {
    return del<void>(`${API_BASE}/copywriting/${id}`);
  },

  // Use copywriting
  use: async (id: number, data: CopywritingUse): Promise<CopywritingUseResponse> => {
    return post<CopywritingUseResponse>(`${API_BASE}/copywriting/${id}/use`, data);
  },

  // Move copywriting
  move: async (id: number, targetFolderId: number | null): Promise<Copywriting> => {
    return post<Copywriting>(`${API_BASE}/copywriting/${id}/move`, {
      target_folder_id: targetFolderId,
    });
  },

  // 获取关联素材
  getMaterials: async (id: number): Promise<MaterialSimple[]> => {
    return get<MaterialSimple[]>(`${API_BASE}/copywriting/${id}/materials`);
  },

  // 关联已有素材
  attachMaterials: async (id: number, materialIds: number[]): Promise<void> => {
    return post<void>(`${API_BASE}/copywriting/${id}/materials`, materialIds);
  },

  // 解除关联
  detachMaterial: async (id: number, materialId: number): Promise<void> => {
    return del<void>(`${API_BASE}/copywriting/${id}/materials/${materialId}`);
  },

  // 上传并关联素材（可指定素材分组）
  uploadAndAttach: async (
    id: number,
    files: File[],
    folderId?: number | null,
  ): Promise<{ uploaded: number; materials: MaterialSimple[] }> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    if (folderId) formData.append('folder_id', String(folderId));
    return post<{ uploaded: number; materials: MaterialSimple[] }>(
      `${API_BASE}/copywriting/${id}/materials/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
};

// Material APIs
export const materialApi = {
  // Upload single material
  upload: async (file: File, data: {
    scope: string;
    folder_id?: number;
    name?: string;
    tags?: string;
  }): Promise<Material> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', data.scope);
    if (data.folder_id) formData.append('folder_id', data.folder_id.toString());
    if (data.name) formData.append('name', data.name);
    if (data.tags) formData.append('tags', data.tags);

    console.log('=== 上传素材 FormData ===');
    console.log('  scope:', data.scope);
    console.log('  folder_id:', data.folder_id);
    console.log('  name:', data.name);
    console.log('  file:', file);

    // 删除默认的 Content-Type，让浏览器自动设置（包含 boundary）
    return post<Material>(`${API_BASE}/materials/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: [(data) => data], // 不转换 FormData
    });
  },

  // Batch upload materials
  uploadBatch: async (files: File[], data: {
    scope: string;
    folder_id?: number;
  }): Promise<Material[]> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('scope', data.scope);
    if (data.folder_id) formData.append('folder_id', data.folder_id.toString());

    return post<Material[]>(`${API_BASE}/materials/upload-batch`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: [(data) => data], // 不转换 FormData
    });
  },

  // Create material
  create: async (data: MaterialCreate): Promise<Material> => {
    return post<Material>(`${API_BASE}/materials`, data);
  },

  // Get material list
  getList: async (params: ResourceQueryParams & { file_type?: string }): Promise<MaterialListResponse> => {
    return get<MaterialListResponse>(`${API_BASE}/materials`, { params });
  },

  // Get material by ID
  getById: async (id: number): Promise<Material> => {
    return get<Material>(`${API_BASE}/materials/${id}`);
  },

  // Update material
  update: async (id: number, data: MaterialUpdate): Promise<Material> => {
    return put<Material>(`${API_BASE}/materials/${id}`, data);
  },

  // Delete material
  delete: async (id: number): Promise<void> => {
    return del<void>(`${API_BASE}/materials/${id}`);
  },

  // Use material
  use: async (id: number): Promise<{ material: Material }> => {
    return post<{ material: Material }>(`${API_BASE}/materials/${id}/use`, {});
  },

  // Move material
  move: async (id: number, targetFolderId: number | null): Promise<Material> => {
    return post<Material>(`${API_BASE}/materials/${id}/move`, {
      target_folder_id: targetFolderId,
    });
  },
};

// Quick Reply APIs
export const quickReplyApi = {
  // Get quick reply list
  getList: async (params: QuickReplyQueryParams): Promise<QuickReplyItem[]> => {
    return get<QuickReplyItem[]>(`${API_BASE}/quick-reply`, { params });
  },

  // Get recent used
  getRecentUsed: async (type: string = 'all', limit: number = 20): Promise<QuickReplyItem[]> => {
    return get<QuickReplyItem[]>(`${API_BASE}/quick-reply/recent-used`, {
      params: { type, limit },
    });
  },

  // Get trending
  getTrending: async (params: TrendingQueryParams): Promise<QuickReplyItem[]> => {
    return get<QuickReplyItem[]>(`${API_BASE}/quick-reply/trending`, { params });
  },
};

const resourceService = {
  folder: folderApi,
  copywriting: copywritingApi,
  material: materialApi,
  quickReply: quickReplyApi,
};

export default resourceService;
